import {useMemo} from 'react'
import {useAppConfig} from '@/hooks/use-app-config'
import {buildCelEngine, buildCertificationCelEngine, buildProfileCelEngine} from './engine-factory'
import type {IamCelSuggestionEngine} from './ruleengine/cel/IamCelSuggestionEngine'

/**
 * Returns the in-browser CEL suggestion engine wired against the current
 * {@link AppConfig} snapshot. Memoised by AppConfig identity — TanStack
 * Query keeps the snapshot stable across renders, so the engine is built
 * once per page and reused for every keystroke.
 */
export function useCelEngine(): IamCelSuggestionEngine {
    const config = useAppConfig()
    return useMemo(() => buildCelEngine(config), [config])
}

/**
 * Like {@link useCelEngine} but builds the restriction-rule (profile)
 * schema — single `profile` root, no IAM domain types.
 */
export function useProfileCelEngine(): IamCelSuggestionEngine {
    const config = useAppConfig()
    return useMemo(() => buildProfileCelEngine(config), [config])
}

/**
 * Like {@link useProfileCelEngine} but builds the certification-policy scope
 * schema — `access` root over the certified assignment spine (epic 924).
 */
export function useCertificationCelEngine(): IamCelSuggestionEngine {
    const config = useAppConfig()
    return useMemo(() => buildCertificationCelEngine(config), [config])
}
